// ================= PART 1 — IMPORTS =================
import { useEffect, useRef, useState, useMemo } from "react";
import { useChatStore } from "../store/useChatStore";
import { useAuthStore } from "../store/useAuthStore";
import ChatHeader from "./ChatHeader";
import MessageInput from "./MessageInput";
import MessageSkeleton from "./skeletons/MessageSkeleton";
import { formatMessageTime } from "../lib/utils";
import { useLayoutEffect } from "react";
import { Check, CheckCheck } from "lucide-react";
import ForwardModal from "./ForwardModal";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import { SERVER_URL } from "../api";
import getFileIcon from "../lib/getFileIcon";
import { useNavigate } from "react-router-dom";
import "./ChatContainer.css";
import ChatSearchBar from "./ChatSearchBar";
import ChatInfoPanel from "./ChatInfoPanel";

dayjs.extend(isToday);
dayjs.extend(isYesterday);

// ====== Announcement IDs (company-scoped) ======
const ANNOUNCE_SDC_ID = "group:announcements:SDC";
const ANNOUNCE_SVB_ID = "group:announcements:SVB";
const LEGACY_ANNOUNCE_ID = "group:announcements";

const isAnnouncementId = (id) =>
  id === ANNOUNCE_SDC_ID || id === ANNOUNCE_SVB_ID || id === LEGACY_ANNOUNCE_ID;

const getCompanyFromThreadId = (id = "") => {
  if (id.endsWith(":SVB")) return "SVB";
  if (id.endsWith(":SDC")) return "SDC";
  return null;
};

// ================= TEXT MARKUP + LINKIFY =================
const linkify = (html = "") =>
  html.replace(
    /((?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?)/gi,
    (m) => {
      const href = m.startsWith("http") ? m : `https://${m}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:black;text-decoration:underline;word-break:break-all;">${m}</a>`;
    },
  );

function parseWhatsAppMarkup(raw = "") {
  if (!raw) return "";

  let text = raw.replace(/\r\n/g, "\n");

  // HTML escape
  text = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const blocks = [];

  // ``` CODE BLOCK ```
  text = text.replace(/```([\s\S]*?)```/g, (_m, code) => {
    const key = `__CODE_${blocks.length}__`;
    blocks.push(
      `<pre style="background:#f6f6f6;border:1px solid #ddd;padding:10px;border-radius:8px;white-space:pre-wrap">${code}</pre>`,
    );
    return key;
  });

  // `inline code`
  text = text.replace(
    /`([^`\n]+)`/g,
    `<code style="background:#eee;padding:3px 5px;border-radius:4px;">$1</code>`,
  );

  // **bold**
  text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  // *bold*
  text = text.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<strong>$1</strong>");

  // _italic_
  text = text.replace(/_([^_]+)_/g, "<em>$1</em>");

  // ~strike~
  text = text.replace(/~([^~]+)~/g, "<s>$1</s>");

  // > quote
  text = text.replace(
    /^> (.*)$/gm,
    `<blockquote style="border-left:3px solid #999;margin:4px 0;padding-left:8px;color:#666">$1</blockquote>`,
  );

  // Restore code blocks
  blocks.forEach((val, i) => {
    text = text.replace(`__CODE_${i}__`, val);
  });

  return text.replace(/\n/g, "<br>");
}

// ================= FILE URL / PROXY HELPERS =================
const decodeLoose = (value = "") => {
  try {
    let out = String(value).replace(/\+/g, " ");
    for (let i = 0; i < 2; i++) {
      const dec = decodeURIComponent(out);
      if (dec === out) break;
      out = dec;
    }
    return out;
  } catch {
    return value;
  }
};

const normalizeUploadPath = (raw = "") => {
  if (!raw) return "";

  let p = raw;
  try {
    const u = new URL(raw, SERVER_URL);
    p = u.pathname;
  } catch {}

  if (p.startsWith("/uploads/")) return p;
  if (p.startsWith("/files/")) return p;

  return "";
};

const buildProxyUrl = (filePath = "", name = "file", inline = false) => {
  if (
    !filePath ||
    (!filePath.startsWith("/uploads/") && !filePath.startsWith("/files/"))
  ) {
    return "";
  }

  return `${SERVER_URL}/api/upload/proxy-download?url=${encodeURIComponent(
    filePath,
  )}&name=${encodeURIComponent(name)}&inline=${inline ? "1" : "0"}`;
};

const fileNameFromUrl = (u = "") => {
  try {
    const asUrl = new URL(u, SERVER_URL);
    let last = asUrl.pathname.split("/").pop() || "file";
    last = decodeLoose(last);
    if (last.includes("/")) last = last.split("/").pop();
    return last || "file";
  } catch {
    const parts = (u || "").split("/");
    let last = decodeLoose(parts.pop() || "file");
    if (last.includes("/")) last = last.split("/").pop();
    return last || "file";
  }
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "Unknown size";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

// ================= IMAGE TYPE HELPERS =================
const IMG_EXTS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "jfif",
  "heic",
  "heif",
  "bmp",
  "tif",
  "tiff",
  "ico",
]);

const isImageExt = (ext = "") => IMG_EXTS.has(ext.toLowerCase());

const isImageMessage = (msg = {}, fileName = "") => {
  const byMime =
    typeof msg?.mimeType === "string" && msg.mimeType.startsWith("image/");
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  return byMime || isImageExt(ext);
};

// ================= SEARCH HIGHLIGHT (only inside text, never inside tags) =================
const highlightHtml = (html = "", q = "") => {
  const term = q.trim();
  if (!term) return html;
  const re = new RegExp(
    `(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  return html
    .split(/(<[^>]+>)/g)
    .map((part) =>
      part.startsWith("<")
        ? part
        : part.replace(re, '<mark class="cc__mark">$1</mark>'),
    )
    .join("");
};

const LINK_RE = /(?:https?:\/\/|www\.)[^\s<]+/gi;

// ================= READ RECEIPTS =================
const RenderTicks = ({ isSentByMe, isRead }) => {
  if (!isSentByMe) return null;
  if (isRead) {
    return <CheckCheck size={14} style={{ color: "#34B7F1" }} />;
  }
  return <Check size={14} style={{ color: "#999" }} />;
};

// ========================== PART 2 — COMPONENT ==========================
const ChatContainer = () => {
  const {
    messages,
    getMessages,
    isMessagesLoading,
    selectedUser,
    subscribeToMessages,
    unsubscribeFromMessages,
  } = useChatStore();

  const { authUser, isCheckingAuth } = useAuthStore();
  const navigate = useNavigate();

  const scrollerRef = useRef(null);
  const messageEndRef = useRef(null);
  const messageRefs = useRef({});
  const [highlightMessageId, setHighlightMessageId] = useState(null);
  // UI states
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchPos, setMatchPos] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    message: null,
  });

  const [imgSrcFallback, setImgSrcFallback] = useState({});

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );

  const forceBottom = () => {
    const sc = scrollerRef.current;
    if (!sc) return;
    sc.scrollTop = sc.scrollHeight;
  };

  // Don't jump to bottom while the user is searching
  useLayoutEffect(() => {
    if (!messages?.length) return;
    if (searchOpen && searchQuery.trim()) return;

    forceBottom();

    requestAnimationFrame(() => {
      forceBottom();
      setTimeout(forceBottom, 100);
      setTimeout(forceBottom, 300);
      setTimeout(forceBottom, 700);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, selectedUser?._id]);

  // close search + info when chat changes
  useEffect(() => {
    setSearchOpen(false);
    setSearchQuery("");
    setInfoOpen(false);
  }, [selectedUser?._id]);

  // ids of messages whose text contains the search term
  const matchIds = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!searchOpen || !q) return [];
    return (messages || [])
      .filter((m) =>
        String(m.content || m.text || "")
          .toLowerCase()
          .includes(q),
      )
      .map((m) => String(m._id));
  }, [messages, searchQuery, searchOpen]);

  // start at the latest match (like WhatsApp)
  useEffect(() => {
    setMatchPos(Math.max(0, matchIds.length - 1));
  }, [searchQuery, matchIds.length]);

  // scroll to the current match
  useEffect(() => {
    if (!searchOpen || !matchIds.length) return;
    const el = messageRefs.current[matchIds[matchPos]];
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [matchPos, matchIds, searchOpen]);

  // media / docs / links built from loaded messages
  const shared = useMemo(() => {
    const media = [];
    const docs = [];
    const links = [];

    (messages || []).forEach((m) => {
      let files = [];
      if (Array.isArray(m.files) && m.files.length > 0) {
        files = m.files;
      } else if (m.file) {
        files = [
          {
            url: m.file?.savedAs
              ? `/files/${m.file.savedAs}`
              : m.file?.url || m.file,
            fileName: m.fileName || "",
            fileType: m.mimeType || "",
          },
        ];
      }

      files.forEach((f, i) => {
        const norm = normalizeUploadPath(f.url);
        const name = f.fileName || fileNameFromUrl(f.url) || "Document";
        const url = buildProxyUrl(norm, name, true);
        if (!url) return;

        const item = {
          id: `${m._id}_${i}`,
          messageId: m._id,
          name,
          url,
          norm,
          createdAt: m.createdAt,
        };
        if (isImageMessage({ mimeType: f.fileType }, name)) media.push(item);
        else docs.push(item);
      });

      const text = String(m.content || m.text || "");
      (text.match(LINK_RE) || []).forEach((u, i) => {
        links.push({
          id: `${m._id}_l${i}`,
          messageId: m._id,
          url: u,
          href: u.startsWith("http") ? u : `https://${u}`,
          createdAt: m.createdAt,
        });
      });
    });

    // newest first
    return {
      media: media.reverse(),
      docs: docs.reverse(),
      links: links.reverse(),
    };
  }, [messages]);

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
  };

  const stepMatch = (dir) => {
    if (!matchIds.length) return;
    setMatchPos((p) => (p + dir + matchIds.length) % matchIds.length);
  };

  const myId = authUser?._id;
  const amAdmin = authUser?.role?.toLowerCase?.() === "admin";
  const isAnnouncementsThread = isAnnouncementId(selectedUser?._id);

  const scrollToReplyMessage = (replyTo) => {
    const replyId = replyTo?._id || replyTo?.id;

    if (!replyId) return;

    const target = messageRefs.current[replyId];

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setHighlightMessageId(replyId);

      setTimeout(() => {
        setHighlightMessageId(null);
      }, 1500);
    }
  };

  // ============== Resize Listener ==============
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ============== Load Messages + Subscribe ==============
  useEffect(() => {
    if (!selectedUser?._id) return;

    getMessages(selectedUser._id);
    subscribeToMessages();

    return () => unsubscribeFromMessages();
  }, [selectedUser, getMessages, subscribeToMessages, unsubscribeFromMessages]);

  // ============== Mark Announcement Read ==============
  useEffect(() => {
    if (!isAnnouncementsThread) return;

    let company = getCompanyFromThreadId(selectedUser._id);
    if (!company) {
      const s = (authUser?.companyName || "").toLowerCase();
      company = s.includes("svb") ? "SVB" : "SDC";
    }

    const controller = new AbortController();
    fetch(`${SERVER_URL}/api/announcements/read`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ company }),
      signal: controller.signal,
    }).catch(() => {});

    return () => controller.abort();
  }, [isAnnouncementsThread, selectedUser?._id, authUser?.companyName]);

  // ============== Close Context Menu ==============
  useEffect(() => {
    const close = () => setContextMenu((p) => ({ ...p, visible: false }));
    const sc = scrollerRef.current;

    window.addEventListener("click", close);
    sc?.addEventListener("scroll", close);

    return () => {
      window.removeEventListener("click", close);
      sc?.removeEventListener("scroll", close);
    };
  }, []);

  // ============== Track Whether User is At Bottom ==============
  // FIX: the scroll element is re-created when the layout switches between
  // "checking auth" / "loading skeleton" / "chat". The listener must be
  // re-attached each time, otherwise isAutoScroll stays true forever and
  // lazy-loaded images keep pulling the view back to the bottom.
  useEffect(() => {
    const sc = scrollerRef.current;
    if (!sc) return;

    const onScroll = () => {
      const delta = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
      setIsAutoScroll(delta < 10);
    };

    sc.addEventListener("scroll", onScroll);
    return () => sc.removeEventListener("scroll", onScroll);
  }, [selectedUser?._id, isMessagesLoading, isCheckingAuth]);

  // (File-size HEAD requests removed: they were slowing image loading
  //  and the result was never shown in the UI.)

  // ============== Download Handler ==============
  const handleDownload = (filePath, originalFileName) => {
    const norm = normalizeUploadPath(filePath);
    const name = (originalFileName || fileNameFromUrl(norm)).trim() || "file";

    const href = buildProxyUrl(norm, name, false);

    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    a.style.display = "none";

    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ============== Delete Handler ==============
  const handleDelete = async (messageId) => {
    try {
      await fetch(`${SERVER_URL}/api/messages/${messageId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      getMessages(selectedUser._id);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  // ============== Image fallback loader ==============
  const handleImgError = async (id, file, name) => {
    try {
      const norm = normalizeUploadPath(file);
      const res = await fetch(buildProxyUrl(norm, name, true));
      if (!res.ok) return;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      setImgSrcFallback((prev) => ({ ...prev, [id]: url }));
    } catch {}
  };

  // ============== Desktop Context Menu ==============
  const handleContextMenu = (e, message) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      message,
    });
  };

  // ============== Mobile Long Press ==============
  const longPressRef = useRef({ timer: null });
  const bindLongPress = (message) => ({
    onTouchStart: (e) => {
      const t = e.touches?.[0];
      longPressRef.current.timer = setTimeout(() => {
        setContextMenu({
          visible: true,
          x: t?.clientX ?? 20,
          y: t?.clientY ?? 20,
          message,
        });
        navigator.vibrate?.(10);
      }, 500);
    },
    onTouchEnd: () => clearTimeout(longPressRef.current.timer),
    onTouchMove: () => clearTimeout(longPressRef.current.timer),
  });

  const handleOpenProject = (projectId, projectName) => {
    if (!projectId) return;

    navigate(
      `/messenger/project-communication?projectId=${projectId}&name=${encodeURIComponent(
        projectName || "",
      )}`,
    );
  };

  // ============== Early returns ==============
  if (isCheckingAuth)
    return <div className="cc__center">Checking user info...</div>;

  if (!selectedUser?._id)
    return (
      <div className="cc__center">Please select a user to start chatting</div>
    );

  // Loading: header + input stay fixed, only the middle shows skeleton
  if (isMessagesLoading)
    return (
      <div className="cc">
        <div className="cc__header">
          <ChatHeader
            selectedUser={selectedUser}
            onSearch={() => {
              setInfoOpen(false);
              setSearchOpen(true);
            }}
            onInfo={() => setInfoOpen(true)}
          />
        </div>
        <div className="cc__scroll" ref={scrollerRef}>
          <MessageSkeleton />
        </div>
        <div className="cc__input">
          <MessageInput replyTo={null} setReplyTo={() => {}} />
        </div>
      </div>
    );

  let lastMessageDate = null;

  // ========================================================================
  // FINAL JSX — FIXED HEADER + FIXED INPUT + ONLY MESSAGE AREA SCROLLS
  // ========================================================================
  return (
    <div className="cc">
      {/* ---------------- FIXED CHAT HEADER ---------------- */}
      <div className="cc__header">
        <ChatHeader
          selectedUser={selectedUser}
          onSearch={() => {
            setInfoOpen(false);
            setSearchOpen(true);
          }}
          onInfo={() => setInfoOpen(true)}
        />
      </div>

      {/* ---------------- SEARCH BAR ---------------- */}
      {searchOpen && (
        <ChatSearchBar
          query={searchQuery}
          setQuery={setSearchQuery}
          total={matchIds.length}
          position={matchPos}
          onPrev={() => stepMatch(-1)}
          onNext={() => stepMatch(1)}
          onClose={closeSearch}
        />
      )}

      {/* ---------------- SCROLLABLE MESSAGES ---------------- */}
      <div className="cc__scroll" ref={scrollerRef}>
        <div className="cc__list">
          {(messages || []).map((message) => {
            const senderId = message.senderId?._id || message.senderId;

            const isAnnouncementMsg =
              message?.scope === "announcement" ||
              isAnnouncementId(message?.receiverId) ||
              isAnnouncementsThread;

            const isSentByMe =
              String(senderId) === String(myId) ||
              (isAnnouncementMsg && amAdmin);

            const currentDate = dayjs(message.createdAt).format("YYYY-MM-DD");
            const showDateSeparator = currentDate !== lastMessageDate;
            lastMessageDate = currentDate;

            const commonPressHandlers = isMobile ? bindLongPress(message) : {};

            const isCurrentMatch =
              searchOpen && matchIds[matchPos] === String(message._id);

            // Always prepare filesArray for both old + new backend
            let filesArray = [];
            if (Array.isArray(message.files) && message.files.length > 0) {
              filesArray = message.files;
            } else if (message.file) {
              filesArray = [
                {
                  url: message.file?.savedAs
                    ? `/files/${message.file.savedAs}`
                    : message.file?.url || message.file,
                  fileName:
                    message.fileName ||
                    (message.file?.savedAs
                      ? message.file.savedAs
                      : fileNameFromUrl(message.file?.url || message.file)),
                  fileType: message.mimeType || "",
                  fileSize: message.fileSize || 0,
                },
              ];
            }

            return (
              <div
                key={message._id}
                ref={(el) => {
                  if (el) messageRefs.current[message._id] = el;
                }}
                onContextMenu={(e) => handleContextMenu(e, message)}
                {...commonPressHandlers}
                className={`cc__item ${
                  highlightMessageId === message._id ? "is-highlight" : ""
                } ${isCurrentMatch ? "is-current-match" : ""}`}
              >
                {/* ---------------- DATE SEPARATOR ---------------- */}
                {showDateSeparator && (
                  <div className="cc__date-wrap">
                    <div className="cc__date">
                      {dayjs(message.createdAt).isToday()
                        ? "Today"
                        : dayjs(message.createdAt).isYesterday()
                          ? "Yesterday"
                          : dayjs(message.createdAt).format("DD/MM/YYYY")}
                    </div>
                  </div>
                )}

                {/* ---------------- MESSAGE ROW ---------------- */}
                <div className={`cc__row ${isSentByMe ? "is-me" : ""}`}>
                  <div className="cc__col">
                    {/* ---------------- REPLY PREVIEW ---------------- */}
                    {message.replyTo && (
                      <div
                        className="cc__reply"
                        onClick={() => scrollToReplyMessage(message.replyTo)}
                        title="Go to replied message"
                      >
                        <div className="cc__reply-title">Replying to:</div>

                        <div
                          dangerouslySetInnerHTML={{
                            __html: (() => {
                              const rt = message.replyTo;

                              if (!rt) return "";

                              if (rt.text) return parseWhatsAppMarkup(rt.text);
                              if (rt.content)
                                return parseWhatsAppMarkup(rt.content);

                              if (rt.fileName) return `📄 ${rt.fileName}`;

                              if (rt.file) return "📄 Document";

                              return "";
                            })(),
                          }}
                        />
                      </div>
                    )}

                    {/* ---------------- FILES (MULTI) ---------------- */}
                    {filesArray.length > 0 && (
                      <div className={`cc__files ${isSentByMe ? "is-me" : ""}`}>
                        {filesArray.map((fObj, i) => {
                          const raw = fObj.url;
                          const normPath = normalizeUploadPath(raw);
                          const fileName =
                            fObj.fileName || fileNameFromUrl(raw) || "Document";

                          const fileExt = (
                            fileName.split(".").pop() || ""
                          ).toLowerCase();
                          const isImage = isImageMessage(
                            { mimeType: fObj.fileType },
                            fileName,
                          );

                          const previewUrl = buildProxyUrl(
                            normPath,
                            fileName,
                            true,
                          );
                          if (!previewUrl) return null;

                          return (
                            <div
                              key={i}
                              className={`cc__file ${isSentByMe ? "is-me" : ""}`}
                            >
                              {/* IMAGE FILE */}
                              {isImage ? (
                                <div>
                                  <a
                                    href={previewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <img
                                      className="cc__img"
                                      loading="lazy"
                                      decoding="async"
                                      alt={fileName}
                                      src={
                                        imgSrcFallback[`${message._id}_${i}`] ||
                                        previewUrl
                                      }
                                      onLoad={(e) => {
                                        e.currentTarget.classList.add(
                                          "is-loaded",
                                        );
                                        if (isAutoScroll) forceBottom();
                                      }}
                                      onError={() =>
                                        handleImgError(
                                          `${message._id}_${i}`,
                                          normPath,
                                          fileName,
                                        )
                                      }
                                    />
                                  </a>

                                  <div className="cc__img-foot">
                                    <button
                                      className="cc__link-btn"
                                      onClick={() =>
                                        handleDownload(normPath, fileName)
                                      }
                                    >
                                      Download
                                    </button>

                                    <div className="cc__meta">
                                      {formatMessageTime(message.createdAt)}
                                      <RenderTicks
                                        isSentByMe={isSentByMe}
                                        isRead={message.isRead}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                /* DOCUMENT FILE (PDF, DOC, XLS, etc.) */
                                <div className="cc__doc">
                                  <div className="cc__doc-top">
                                    <div className="cc__doc-icon">
                                      {getFileIcon(fileExt)}
                                    </div>

                                    <div className="cc__doc-info">
                                      <div className="cc__doc-name">
                                        {fileName}
                                      </div>

                                      <div className="cc__doc-sub">
                                        {(fObj.pageCount || 1) +
                                          " pages • " +
                                          fileExt.toUpperCase() +
                                          " • " +
                                          formatFileSize(
                                            fObj.fileSize || fObj.size || 0,
                                          )}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="cc__doc-actions">
                                    <a
                                      className="cc__link-btn"
                                      href={previewUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Preview
                                    </a>

                                    <span
                                      className="cc__link-btn"
                                      onClick={() =>
                                        handleDownload(normPath, fileName)
                                      }
                                    >
                                      Download
                                    </span>
                                  </div>

                                  <div className="cc__doc-time">
                                    {formatMessageTime(message.createdAt)}
                                    <RenderTicks
                                      isSentByMe={isSentByMe}
                                      isRead={message.isRead}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ---------------- TEXT BUBBLE ---------------- */}
                    {(message.text || message.content) && (
                      <div
                        className={`cc__bubble ${isSentByMe ? "is-me sent-bubble" : ""}`}
                      >
                        {/* Project Name */}
                        {message.projectName && (
                          <div
                            className="cc__project"
                            onClick={() =>
                              handleOpenProject(
                                message.projectId ||
                                  message.project ||
                                  message.projectRef,
                                message.projectName,
                              )
                            }
                          >
                            📁 {message.projectName}
                          </div>
                        )}

                        {/* Text Content */}
                        <div
                          className="cc__text wa-text"
                          dangerouslySetInnerHTML={{
                            __html: highlightHtml(
                              linkify(
                                parseWhatsAppMarkup(
                                  message.content || message.text,
                                ),
                              ),
                              searchOpen ? searchQuery : "",
                            ),
                          }}
                        />

                        <div className="cc__time">
                          {formatMessageTime(message.createdAt)}
                          <RenderTicks
                            isSentByMe={isSentByMe}
                            isRead={message.isRead}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <div ref={messageEndRef} />
        </div>
      </div>

      {/* ---------------- CONTEXT MENU ---------------- */}
      {contextMenu.visible && (
        <div
          className="cc__menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="cc__menu-btn"
            onClick={() => setReplyTo(contextMenu.message)}
          >
            Reply
          </button>

          <button
            className="cc__menu-btn"
            onClick={() =>
              navigator.clipboard.writeText(
                contextMenu.message.text ||
                  contextMenu.message.content ||
                  contextMenu.message.fileName ||
                  "Copied",
              )
            }
          >
            Copy
          </button>

          <button
            className="cc__menu-btn"
            onClick={() => {
              setForwardMessage(contextMenu.message);
              setShowForwardModal(true);
            }}
          >
            Forward
          </button>

          <button
            className="cc__menu-btn"
            onClick={() => handleDelete(contextMenu.message._id)}
          >
            Delete
          </button>
        </div>
      )}

      {/* ---------------- FIXED INPUT BAR ---------------- */}
      {isAnnouncementsThread && !amAdmin ? (
        <div className="cc__announce">
          Only admins can post in Announcements
        </div>
      ) : (
        <div className="cc__input">
          <MessageInput replyTo={replyTo} setReplyTo={setReplyTo} />
        </div>
      )}

      {/* ---------------- MEDIA / DOCS / LINKS PANEL ---------------- */}
      {infoOpen && (
        <ChatInfoPanel
          user={selectedUser}
          media={shared.media}
          docs={shared.docs}
          links={shared.links}
          onClose={() => setInfoOpen(false)}
          onJump={(id) => {
            scrollToReplyMessage({ _id: id });
            if (isMobile) setInfoOpen(false);
          }}
          onDownload={handleDownload}
        />
      )}

      {/* ---------------- FORWARD MODAL ---------------- */}
      <ForwardModal
        isOpen={showForwardModal && !!forwardMessage}
        onClose={() => setShowForwardModal(false)}
        message={forwardMessage}
      />
    </div>
  );
};

export default ChatContainer;
